import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Mark } from "../components/Mark";
import { InlineNote, Notice, buttonClasses } from "../components/primitives";
import { api } from "../lib/api";
import { BRAND } from "../lib/brand";

/**
 * The landing page, and the only one a signed-out visitor can reach.
 *
 * It used to be a form behind a separate `/welcome` that showed the workflow
 * tiles to strangers. Those tiles are a choice, and a choice made before
 * anybody knows whether you may run anything is a choice that gets thrown
 * away — so they moved behind the guard, and this became the front door. The
 * value of the product is stated here; what to do with it comes after
 * signing in.
 *
 * **The mark draws itself.** A surface somebody arrives at, mounted once —
 * the same rule that keeps it off the application bar, where it would replay
 * on every route change.
 *
 * Sign-in is a full-page navigation rather than fetch: the provider redirects
 * the browser, so it cannot be an XHR. The buttons are plain links for that
 * reason.
 */
export function Login() {
  const [params] = useSearchParams();
  const error = params.get("error");
  // Carried from a landing tile. Appended to the provider link and sent with
  // an emailed link request, so the choice survives either way in. The server
  // checks it against the playbook registry before storing it.
  const playbook = params.get("playbook");
  const providers = useQuery({ queryKey: ["auth-providers"], queryFn: api.authProviders });

  const [email, setEmail] = useState("");
  const link = useMutation({
    mutationFn: () => api.requestLoginLink(email.trim(), playbook),
  });

  const providerHref = (key: string) =>
    `/api/auth/login/${key}` +
    (playbook ? `?playbook=${encodeURIComponent(playbook)}` : "");

  return (
    <div className="flex min-h-dvh items-center justify-center px-5 py-16">
      <div className="w-full max-w-sm">
        <div className="mb-12 flex flex-col items-center text-center">
          <Mark size={140} animate loop />
          {/*
            The mark's own line, and the only surface with room for it. It
            rises once and then stays: text that blinks is text nobody
            finishes reading, which is why the loop above stops at the mark.
          */}
          <span className="mark-caption label mt-3">
            Connecting the dots
          </span>
          <h1 className="mt-7 text-3xl font-light tracking-tight text-ink">
            {BRAND.name}
          </h1>
          {/* The one place the tagline opens a sentence. `brand.json` states
              it lower case and a generator must not decide how a sentence
              starts, so the capital is made here, in CSS. */}
          <p className="mt-7 text-base leading-relaxed text-ink first-letter:uppercase">
            {BRAND.tagline}, run end to end.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Every claim in the deliverable carries a finding, every finding
            carries its sources, and every source is checked against the pages
            that were actually retrieved.
          </p>
          <p className="mt-6 text-sm text-ink-2">
            Sign in with a work account to continue.
          </p>
        </div>

        {error && (
          <Notice tone="critical" className="mb-6">
            {error}
          </Notice>
        )}

        {providers.isLoading && (
          <p className="text-center text-sm text-muted">Checking sign-in options…</p>
        )}

        {providers.data && !providers.data.configured && (
          <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-ink-2">
            <p className="font-medium text-ink">No sign-in is configured.</p>
            <p className="mt-1.5 leading-relaxed">
              Set <code className="font-mono text-xs">CA_OAUTH_GOOGLE_CLIENT_ID</code>{" "}
              and its secret (or the Microsoft or GitHub pair) in the server's
              environment, then restart.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {providers.data?.providers.map((provider) => (
            <a
              key={provider.key}
              href={providerHref(provider.key)}
              className={`${buttonClasses("secondary")} w-full`}
            >
              Continue with {provider.label}
            </a>
          ))}
        </div>

        {/*
          The providers come first and the address field sits beneath them,
          visible rather than behind a disclosure. Leading with the email box
          would train people away from the stronger method — for a work account
          the directory is the authority and the address cannot be
          self-asserted, whereas a link proves control of a mailbox and nothing
          more. Hiding it would make it useless to the person who needs it.
        */}
        {providers.data?.email_login && (
          <div className="mt-6">
            {providers.data.providers.length > 0 && (
              <div className="mb-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" />
                <span className="label">
                  or
                </span>
                <span className="h-px flex-1 bg-line" />
              </div>
            )}

            {link.isSuccess ? (
              <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-ink-2">
                <p className="font-medium text-ink">Check your inbox.</p>
                <p className="mt-1.5 leading-relaxed">{link.data.detail}</p>
                {/* The case nothing used to answer: the mail never came. A
                    second press is a second link, and the server says so
                    plainly when there have been too many. */}
                <p className="mt-3 text-xs leading-relaxed">
                  Nothing after a couple of minutes? Look in spam, then{" "}
                  <button
                    type="button"
                    className="font-medium text-ink underline underline-offset-2"
                    onClick={() => link.reset()}
                  >
                    send another
                  </button>
                  .
                </p>
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  link.mutate();
                }}
                className="flex flex-col gap-2.5"
              >
                <label className="field-label" htmlFor="login-email">
                  Work email
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  className="field"
                  placeholder="you@firm.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <button
                  type="submit"
                  disabled={link.isPending || email.trim() === ""}
                  className={buttonClasses("secondary")}
                >
                  {link.isPending ? "Sending…" : "Email me a sign-in link"}
                </button>
                {link.isError && (
                  <InlineNote tone="critical">{(link.error as Error).message}</InlineNote>
                )}
              </form>
            )}
          </div>
        )}

        <p className="mt-8 text-center text-xs leading-relaxed text-muted">
          New accounts are reviewed by an administrator before they can run an
          engagement.
        </p>
      </div>
    </div>
  );
}
